import type { TypeCode } from './types'

export interface TypeInterpretation {
  code: TypeCode
  name: string
  tagline: string
  description: string
  coreTraits: string[]
  strengths: string[]
  challenges: string[]
  idealEnvironment: string[]
  stressResponse: string
  growthTips: string[]
}

export const TYPE_INTERPRETATIONS: Record<TypeCode, TypeInterpretation> = {
  // ASID - 逻辑自治型
  ASID: {
    code: 'ASID',
    name: '逻辑自治型',
    tagline: '独立思考的系统构建者',
    description: '你依据内在标准做判断，用系统化方式处理问题，由内在兴趣驱动，喜欢明确的方向。你是一个独立、有条理、专注的人，善于深入钻研自己认为重要的领域。',
    coreTraits: [
      '有清晰的内在评价标准，不轻易被外界动摇',
      '善于构建框架和方法论',
      '对感兴趣的事能长期专注投入',
      '喜欢有明确目标和计划',
    ],
    strengths: [
      '独立思考能力强，不随波逐流',
      '能建立系统性的知识体系',
      '执行力强，目标明确时效率高',
      '在专业领域能达到很深的造诣',
    ],
    challenges: [
      '可能忽略有价值的外部反馈',
      '面对模糊或快速变化的环境时可能不适',
      '可能给人坚持己见的印象',
    ],
    idealEnvironment: [
      '允许深度专注和独立工作',
      '有明确的目标和评价标准',
      '尊重专业能力和独立见解',
    ],
    stressResponse: '可能更加封闭，坚持自己的方式，减少与外界的沟通',
    growthTips: [
      '练习主动寻求和考虑外部反馈',
      '在某些情况下尝试保持开放，不急于下结论',
      '培养对模糊情境的容忍度',
    ],
  },

  // ASIO - 探索建构型
  ASIO: {
    code: 'ASIO',
    name: '探索建构型',
    tagline: '好奇心驱动的系统探索者',
    description: '你有独立的判断标准，用系统化方式思考，被内在好奇心驱动，同时保持开放探索的态度。你善于在感兴趣的领域构建知识体系，同时愿意不断扩展边界。',
    coreTraits: [
      '有内在的价值判断标准',
      '喜欢结构化地理解事物',
      '由兴趣和好奇心驱动',
      '愿意探索新可能，不急于锁定方向',
    ],
    strengths: [
      '能在深度和广度之间取得平衡',
      '善于发现不同领域之间的联系',
      '创新性强，愿意尝试新方法',
      '学习能力强，能持续成长',
    ],
    challenges: [
      '可能同时关注太多方向，难以聚焦',
      '决策时可能犹豫不决',
      '可能忽视实际产出和外部期待',
    ],
    idealEnvironment: [
      '允许探索和实验',
      '有自主空间，不过度干预',
      '鼓励创新和独立思考',
    ],
    stressResponse: '可能更加沉浸在自己的思考中，用分析和探索来应对压力',
    growthTips: [
      '设定阶段性目标，避免无限发散',
      '学会在必要时做出决定并推进',
      '适当关注外部反馈和实际影响',
    ],
  },

  // ASED - 战略统筹型
  ASED: {
    code: 'ASED',
    name: '战略统筹型',
    tagline: '目标导向的独立规划者',
    description: '你有自己的判断标准，系统化地处理问题，被结果和成就驱动，喜欢明确的方向。你是一个有战略眼光、执行力强的人，善于规划并达成目标。',
    coreTraits: [
      '有清晰的自我评价标准',
      '善于制定计划和策略',
      '目标导向，追求结果',
      '喜欢确定性，快速决策',
    ],
    strengths: [
      '战略规划能力强',
      '执行力和推动力强',
      '能独立做出判断并承担责任',
      '在竞争环境中表现出色',
    ],
    challenges: [
      '可能过于关注结果而忽视过程',
      '可能给人果断强势的印象',
      '可能忽略团队成员的感受',
    ],
    idealEnvironment: [
      '有明确目标和成长空间',
      '能发挥独立决策权',
      '结果导向，奖励成就',
    ],
    stressResponse: '可能更加专注于目标，加倍努力，减少无关活动',
    growthTips: [
      '关注过程和他人感受，不只看结果',
      '在某些情况下保持开放，考虑更多可能性',
      '学会适当依赖和信任团队',
    ],
  },

  // ASEO - 系统创新型
  ASEO: {
    code: 'ASEO',
    name: '系统创新型',
    tagline: '追求成就的创新架构师',
    description: '你有独立的判断标准，系统化地思考问题，被成就和认可驱动，同时保持开放探索。你善于在追求目标的同时保持创新，能够构建新的系统和方法。',
    coreTraits: [
      '有自己的评价标准和方向感',
      '善于结构化思考和规划',
      '追求有影响力的成果',
      '愿意探索新方法和可能性',
    ],
    strengths: [
      '能将创新想法系统化落地',
      '既有战略眼光又保持灵活',
      '善于发现并抓住机会',
      '能在变化中保持方向感',
    ],
    challenges: [
      '可能同时追求太多目标',
      '创新和执行之间可能产生张力',
      '对目标的追求可能带来较大压力',
    ],
    idealEnvironment: [
      '鼓励创新和成就',
      '有挑战性的目标',
      '允许尝试新方法',
    ],
    stressResponse: '可能寻找新的解决方案，同时加强对局面的掌控',
    growthTips: [
      '学会聚焦，避免分散精力',
      '平衡创新探索和实际执行',
      '适当降低对自己的要求',
    ],
  },

  // ACID - 直觉决断型
  ACID: {
    code: 'ACID',
    name: '直觉决断型',
    tagline: '果断独立的直觉行动者',
    description: '你依据内在标准判断，用直觉和经验处理问题，被内在动机驱动，喜欢快速明确方向。你是一个果断、独立、行动力强的人，善于快速做出决定并推进。',
    coreTraits: [
      '有清晰的内在判断标准',
      '依靠直觉和经验做决策',
      '由内在动机驱动',
      '喜欢快速确定方向',
    ],
    strengths: [
      '决策速度快，行动力强',
      '能在信息不完整时做出判断',
      '独立自主，不依赖他人',
      '对自己的选择有信心',
    ],
    challenges: [
      '可能过于依赖直觉，忽视分析',
      '决策过快可能遗漏重要信息',
      '可能给人果断坚定的印象',
    ],
    idealEnvironment: [
      '允许快速决策和行动',
      '不过度强调流程和分析',
      '尊重个人判断和自主性',
    ],
    stressResponse: '可能更加依赖直觉，快速做出决定，减少犹豫',
    growthTips: [
      '在重大决策前增加分析和验证',
      '学会听取不同意见',
      '培养对复杂情况的耐心',
    ],
  },

  // ACIO - 灵性体验型
  ACIO: {
    code: 'ACIO',
    name: '灵性体验型',
    tagline: '追随内心的自由探索者',
    description: '你有内在的价值标准，用直觉感受世界，被兴趣和意义驱动，保持开放探索的态度。你是一个追求内心自由、重视体验的人，善于感知和创造意义。',
    coreTraits: [
      '有独立的价值观和判断标准',
      '善于直觉感知和体验',
      '追求意义和兴趣',
      '保持开放，享受探索过程',
    ],
    strengths: [
      '创造力和感受力强',
      '能发现他人忽略的可能性',
      '不被世俗标准束缚',
      '能给他人带来启发和新视角',
    ],
    challenges: [
      '可能难以适应结构化环境',
      '可能较少关注实际产出',
      '可能难以坚持长期规划',
    ],
    idealEnvironment: [
      '允许自由表达和探索',
      '不过度强调效率和结果',
      '重视创意和个人价值',
    ],
    stressResponse: '可能寻求独处，通过内在探索来恢复能量',
    growthTips: [
      '在追求意义的同时关注现实需求',
      '建立一些基本的结构和习惯',
      '学会将想法转化为行动',
    ],
  },

  // ACED - 实用主义型
  ACED: {
    code: 'ACED',
    name: '实用主义型',
    tagline: '独立务实的效率追求者',
    description: '你有自己的判断标准，用灵活实用的方式处理问题，被结果和回报驱动，喜欢明确方向。你是一个务实、高效、目标明确的人，善于用最少的投入获得最大的产出。',
    coreTraits: [
      '有自己的评价标准，不盲从',
      '灵活务实，讲究方法',
      '重视投入产出比',
      '喜欢明确的目标和结果',
    ],
    strengths: [
      '务实高效，不做无用功',
      '灵活应变，善于找捷径',
      '目标明确，执行力强',
      '善于资源整合和优化',
    ],
    challenges: [
      '可能更关注短期回报，较少考虑长期积累',
      '可能不愿投入看不到回报的事',
      '可能在某些领域投入较少时间深耕',
    ],
    idealEnvironment: [
      '结果导向，奖励效率',
      '允许灵活的工作方式',
      '有明确的回报机制',
    ],
    stressResponse: '可能更加务实，砍掉不重要的事，聚焦核心目标',
    growthTips: [
      '关注一些长期但暂时看不到回报的投入',
      '适当深入某些领域',
      '平衡效率和质量',
    ],
  },

  // ACEO - 自在随性型
  ACEO: {
    code: 'ACEO',
    name: '自在随性型',
    tagline: '随遇而安的灵活行动者',
    description: '你有内在的判断标准，用灵活的方式应对，被结果驱动但保持开放。你是一个自在、灵活、善于抓住机会的人，能在变化中找到自己的节奏。',
    coreTraits: [
      '有自己的判断标准，但不固执',
      '灵活应变，随机应变',
      '追求结果但不执着',
      '保持开放，接受变化',
    ],
    strengths: [
      '适应能力极强',
      '善于抓住机会',
      '压力下保持冷静',
      '能在混乱中找到路径',
    ],
    challenges: [
      '可能缺乏长期规划',
      '可能给人随性的印象',
      '可能难以深入坚持',
    ],
    idealEnvironment: [
      '变化多、机会多',
      '不过度强调计划和流程',
      '允许灵活调整方向',
    ],
    stressResponse: '保持灵活，寻找新的出路，不会陷入死结',
    growthTips: [
      '建立一些长期目标和计划',
      '在某些领域深入发展',
      '学会适当的坚持',
    ],
  },

  // RSID - 学术钻研型
  RSID: {
    code: 'RSID',
    name: '学术钻研型',
    tagline: '严谨深入的知识追求者',
    description: '你参考外部标准，用系统化方式研究，被内在求知欲驱动，喜欢明确方向。你是一个严谨、专注、追求深度的人，善于在专业领域达到高水平。',
    coreTraits: [
      '重视外部反馈和权威标准',
      '系统化地学习和研究',
      '由求知欲和兴趣驱动',
      '喜欢明确的方向和标准',
    ],
    strengths: [
      '专业能力强，追求深度',
      '严谨细致，注重质量',
      '能长期专注于感兴趣的领域',
      '善于吸收和整合知识',
    ],
    challenges: [
      '重视已验证的知识和方法，较少挑战既有框架',
      '可能在需要快速行动时犹豫',
      '可能忽视实际应用价值',
    ],
    idealEnvironment: [
      '学术或研究导向',
      '有明确的标准和反馈机制',
      '允许深入钻研',
    ],
    stressResponse: '可能更加依赖已有的知识框架，寻求权威指导',
    growthTips: [
      '培养独立判断能力',
      '关注知识的实际应用',
      '在某些情况下勇于尝试新方法',
    ],
  },

  // RSIO - 开放学习型
  RSIO: {
    code: 'RSIO',
    name: '开放学习型',
    tagline: '兼容并蓄的终身学习者',
    description: '你参考外部反馈，用系统化方式学习，被内在兴趣驱动，保持开放探索。你是一个好学、开放、善于成长的人，能够持续吸收新知识并整合到自己的体系中。',
    coreTraits: [
      '善于吸收外部信息和反馈',
      '有系统化的学习方法',
      '由好奇心和兴趣驱动',
      '保持开放，愿意更新认知',
    ],
    strengths: [
      '学习能力强，成长快',
      '能够整合不同来源的知识',
      '谦虚开放，善于合作',
      '适应能力强',
    ],
    challenges: [
      '在形成独立观点前倾向于广泛收集信息',
      '可能难以做出明确决策',
      '可能过度关注学习而忽视行动',
    ],
    idealEnvironment: [
      '学习资源丰富',
      '有高质量的反馈和指导',
      '鼓励探索和成长',
    ],
    stressResponse: '可能寻求更多信息和建议，通过学习来应对不确定性',
    growthTips: [
      '培养自己的立场和判断',
      '在学习的同时注重实践',
      '学会在信息不完整时做决定',
    ],
  },

  // RSED - 专业深耕型
  RSED: {
    code: 'RSED',
    name: '专业深耕型',
    tagline: '追求卓越的专业精进者',
    description: '你参考外部标准，用系统化方式工作，被成就和认可驱动，喜欢明确方向。你是一个专业、高效、追求卓越的人，善于在职业领域达到高水平并获得认可。',
    coreTraits: [
      '重视专业标准和能力认可',
      '系统化地发展专业能力',
      '追求专业成就和成长',
      '喜欢清晰的发展路径',
    ],
    strengths: [
      '专业能力突出',
      '职业发展路径清晰',
      '执行力强，结果导向',
      '善于获得专业认可',
    ],
    challenges: [
      '对外部评价比较敏感',
      '可能被既有路径限制',
      '可能较少关注内在兴趣',
    ],
    idealEnvironment: [
      '专业化、有明确成长路径',
      '重视能力，认可成就',
      '有清晰的行业标准',
    ],
    stressResponse: '可能更加努力工作，寻求通过业绩来缓解压力',
    growthTips: [
      '关注内在满足感，平衡外部认可',
      '探索既有路径之外的可能性',
      '培养独立的价值判断',
    ],
  },

  // RSEO - 协作创新型
  RSEO: {
    code: 'RSEO',
    name: '协作创新型',
    tagline: '整合资源的创新推动者',
    description: '你参考外部反馈，用系统化方式工作，追求成就和认可，同时保持开放探索。你善于整合外部资源和意见，在追求目标的同时保持创新。',
    coreTraits: [
      '善于吸收和整合外部信息',
      '有系统化的工作方法',
      '追求有影响力的成果',
      '保持开放，愿意尝试新方法',
    ],
    strengths: [
      '善于协作和整合资源',
      '能推动创新落地',
      '有战略眼光又不失灵活',
      '善于在复杂环境中找到机会',
    ],
    challenges: [
      '可能过度关注外部反馈',
      '可能在多种意见中迷失方向',
      '可能追求太多目标',
    ],
    idealEnvironment: [
      '协作氛围好',
      '鼓励创新和成就',
      '有丰富的资源和机会',
    ],
    stressResponse: '可能寻求外部支持和意见，通过合作来解决问题',
    growthTips: [
      '培养独立判断的能力',
      '学会聚焦核心目标',
      '在必要时坚持自己的方向',
    ],
  },

  // RCID - 务实执行型
  RCID: {
    code: 'RCID',
    name: '务实执行型',
    tagline: '可靠高效的实干者',
    description: '你参考外部反馈，用灵活实用的方式做事，被内在动机驱动，喜欢明确方向。你是一个可靠、务实、执行力强的人，善于把事情做成。',
    coreTraits: [
      '重视外部反馈和团队协作',
      '灵活务实，讲究方法',
      '由内在的责任感驱动',
      '喜欢明确的任务和方向',
    ],
    strengths: [
      '执行力强，可靠',
      '善于协作和沟通',
      '灵活应变，解决问题能力强',
      '能够落地执行',
    ],
    challenges: [
      '在缺乏明确方向时可能需要更多支持',
      '可能不善于战略规划',
      '可能忽视自己的想法',
    ],
    idealEnvironment: [
      '任务明确，有清晰指引',
      '团队协作氛围好',
      '重视执行和结果',
    ],
    stressResponse: '可能更加务实，聚焦于完成手头任务',
    growthTips: [
      '培养独立思考和规划能力',
      '表达自己的想法和见解',
      '关注长期发展，不只是眼前任务',
    ],
  },

  // RCIO - 社交探索型
  RCIO: {
    code: 'RCIO',
    name: '社交探索型',
    tagline: '善于连接的好奇探索者',
    description: '你参考外部反馈，用灵活的方式应对，被内在兴趣驱动，保持开放探索。你是一个善于社交、好奇开放的人，善于在人际互动中发现新的可能性。',
    coreTraits: [
      '重视他人反馈和人际关系',
      '灵活应变，善于沟通',
      '由好奇心和兴趣驱动',
      '保持开放，享受探索',
    ],
    strengths: [
      '社交能力强',
      '善于发现新机会和连接',
      '适应能力强',
      '能给团队带来活力',
    ],
    challenges: [
      '可能过于分散注意力',
      '可能缺乏深度',
      '可能难以长期专注',
    ],
    idealEnvironment: [
      '社交机会多',
      '允许探索和尝试',
      '氛围开放活跃',
    ],
    stressResponse: '可能通过社交和探索新可能来缓解压力',
    growthTips: [
      '在某些领域建立深度',
      '学会专注和坚持',
      '培养独立判断能力',
    ],
  },

  // RCED - 团队协作型
  RCED: {
    code: 'RCED',
    name: '团队协作型',
    tagline: '目标导向的团队推动者',
    description: '你参考外部反馈，用灵活的方式工作，被结果和认可驱动，喜欢明确方向。你是一个善于协作、目标明确、执行力强的人，善于在团队中推动目标达成。',
    coreTraits: [
      '重视团队反馈和认可',
      '灵活务实，善于协调',
      '追求结果和成就',
      '喜欢明确的目标和方向',
    ],
    strengths: [
      '团队协作能力强',
      '执行力强，结果导向',
      '善于协调和推动',
      '能够达成团队目标',
    ],
    challenges: [
      '在缺乏团队支持时可能需要调整适应',
      '更擅长协作环境，独立工作时可能需要适应',
      '可能忽视个人长期发展',
    ],
    idealEnvironment: [
      '团队协作为主',
      '目标明确，奖励成果',
      '有清晰的反馈机制',
    ],
    stressResponse: '可能寻求团队支持，通过协作来解决问题',
    growthTips: [
      '培养独立工作的能力',
      '关注个人的长期发展',
      '建立自己的判断标准',
    ],
  },

  // RCEO - 表演情境型
  RCEO: {
    code: 'RCEO',
    name: '表演情境型',
    tagline: '魅力四射的情境适应者',
    description: '你参考外部反馈，用灵活的方式应对，被结果和认可驱动，保持开放探索。你是一个魅力十足、适应力强的人，善于在不同情境中展现最好的自己。',
    coreTraits: [
      '敏感于外部反馈和环境',
      '高度灵活，善于应变',
      '追求认可和影响力',
      '保持开放，善于把握机会',
    ],
    strengths: [
      '适应能力极强',
      '社交和表现能力强',
      '善于抓住机会',
      '能在各种环境中生存和发展',
    ],
    challenges: [
      '自我表达可能随情境变化较大',
      '对外部反馈比较敏感',
      '可能难以深入和坚持',
    ],
    idealEnvironment: [
      '变化多、机会多',
      '社交和表现机会丰富',
      '奖励灵活和适应能力',
    ],
    stressResponse: '可能快速调整策略，寻找新的出路和支持',
    growthTips: [
      '建立稳定的自我认同和价值观',
      '在某些领域深入发展',
      '减少对外部认可的依赖',
    ],
  },
}

// 获取类型解读的便捷函数
export function getTypeInterpretation(code: TypeCode): TypeInterpretation | null {
  return TYPE_INTERPRETATIONS[code] || null
}

// 获取所有类型代码列表
export const ALL_TYPE_CODES: TypeCode[] = [
  'ASID', 'ASIO', 'ASED', 'ASEO',
  'ACID', 'ACIO', 'ACED', 'ACEO',
  'RSID', 'RSIO', 'RSED', 'RSEO',
  'RCID', 'RCIO', 'RCED', 'RCEO',
]
